import classNames from 'classnames';
import React from 'react';
import {connect} from 'react-redux';

import {Container, Grid} from 'constants/SampleConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import GridCells from 'samples/grids/GridCells';
import {filterChangesToUpdatesOnly} from 'samples/grids/upload-containers/parseChanges';
import {GridCellProps} from 'samples/reduceSamplesUI';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useOnMount from 'ui-library/hooks/useOnMount';
import Modal from 'ui-library/modals/Modal';

import {Change, ChangeStats, MOVE_OUTCOMES, MOVE_OUTCOMES_ORDER} from './UploadContainersFlowConstants';
import {goToPrev, goToNext, uploadFromPlateMap} from './UploadContainersReduxActions';

interface GridCellLabelProps {
  label: string;
  className: string;
  dataTestAttrs?: DataTestAttrs;
}

function GridCellLabel({label, className, dataTestAttrs}: GridCellLabelProps): JSX.Element {
  return (
    <span
      className="u-vertical-center-items u-small-spacing-bottom u-small-spacing-top"
      {...getTestProps({component: 'GridCellLabel', element: 'root', ...dataTestAttrs})}
    >
      <div className={classNames('gridCell--legend', 'u-mini-spacing-right', className)} />
      {label}
    </span>
  );
}

interface MoveSummaryLegendProps {
  changeStats: ChangeStats | null;
}

function MoveSummaryLegend({changeStats}: MoveSummaryLegendProps): JSX.Element {
  return (
    <div {...getTestProps({component: 'MoveSummaryLegend', element: 'root'})}>
      <span className="legacy-font-proxima-14">Summary</span>
      {MOVE_OUTCOMES_ORDER.map((key) => (
        <GridCellLabel
          key={MOVE_OUTCOMES[key].name}
          label={`${MOVE_OUTCOMES[key].name}: ${changeStats?.[key] ?? ''}`}
          className={MOVE_OUTCOMES[key].gridCellClassName}
        />
      ))}
    </div>
  );
}

export interface MoveSummaryModalProps {
  onRequestClose: () => void;
  onSubmit: (id: string) => void;
  title: string;
  sampleStatusWarningParams: {
    checkForWarnings: (containers: Array<Container>) => void;
    renderPossibleWarning: () => JSX.Element | null;
    hasBlockingSampleStatusWarnings: () => boolean;
  };
  // Provided by store
  changes: Array<Change> | null;
  changeStats: ChangeStats | null;
  gridCellProps: Array<Array<GridCellProps>> | null;
  gridPreview: Grid | null;
  isUploading: boolean;
  onGoToPrevious: () => void;
  onGoToNext: () => void;
}

function MoveSummaryModal({
  changes,
  changeStats,
  isUploading,
  gridCellProps,
  gridPreview,
  onGoToPrevious,
  onGoToNext,
  onRequestClose,
  onSubmit,
  title,
  sampleStatusWarningParams,
}: MoveSummaryModalProps): JSX.Element {
  useOnMount(() => {
    const changesWithUpdates: Array<Change> = filterChangesToUpdatesOnly(changes ?? []);
    const containersWithUpdates = changesWithUpdates.map((change) => change.container);
    sampleStatusWarningParams.checkForWarnings(containersWithUpdates);
  });

  const handleGoToNext = (): void => {
    onGoToNext();
  };
  const handleSubmit = (): void => {
    onSubmit(gridPreview!.id);
  };
  const hasOrphans = changeStats && changeStats.moveOut > 0;
  const nextButton = hasOrphans
    ? {
        disabled: sampleStatusWarningParams.hasBlockingSampleStatusWarnings(),
        style: ButtonStyle.SECONDARY,
        text: 'Next',
        onClick: handleGoToNext,
        dataTestAttrs: {component: 'MoveSummaryModal', key: ModalButtonKey.NEXT},
      }
    : {
        disabled: isUploading || sampleStatusWarningParams.hasBlockingSampleStatusWarnings(),
        style: ButtonStyle.PRIMARY,
        text: isUploading ? 'Uploading...' : 'Submit',
        submit: true,
      };
  return (
    <Modal
      buttons={[
        {
          pullLeft: true,
          style: ButtonStyle.TERTIARY_GRAY,
          text: 'Back',
          onClick: onGoToPrevious,
        },
        nextButton,
      ]}
      closeableOnEscapeKey={false}
      onRequestClose={onRequestClose}
      onRequestSubmit={!hasOrphans && gridPreview ? handleSubmit : null}
      title={title}
      dataTestAttrs={{component: 'MoveSummaryModal', element: 'root'}}
    >
      <>
        {sampleStatusWarningParams.renderPossibleWarning()}
        <div className="u-flex-display">
          <MoveSummaryLegend changeStats={changeStats} />
          <GridCells
            className="gridCells-container--white u-flex-grow"
            grid={gridPreview!}
            gridCellProps={gridCellProps}
            showFilledCells={false}
          />
        </div>
      </>
    </Modal>
  );
}

function mapStateToProps(
  state: BenchlingState
): Pick<MoveSummaryModalProps, 'changes' | 'changeStats' | 'gridPreview' | 'gridCellProps' | 'isUploading'> {
  const {changes, changeStats, gridPreview, gridCellProps, isUploading} =
    state.ui.samples.uploadContainersFlow;
  return {
    changes,
    changeStats,
    gridCellProps,
    gridPreview,
    isUploading,
  };
}

export default connect(mapStateToProps, {
  onGoToPrevious: goToPrev,
  onGoToNext: goToNext,
  onSubmit: uploadFromPlateMap,
})(MoveSummaryModal);
